import { Component, EventEmitter, Input, Output } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { MessageService } from 'primeng/api';
import { ButtonModule } from 'primeng/button';
import { DialogModule } from 'primeng/dialog';
import { IconField } from 'primeng/iconfield';
import { IftaLabelModule } from 'primeng/iftalabel';
import { InputIcon } from 'primeng/inputicon';
import { InputTextModule } from 'primeng/inputtext';
import { MultiSelectModule } from 'primeng/multiselect';
import { Select } from 'primeng/select';
import { Textarea } from 'primeng/textarea';
import SampleFirmwareInstallationStatus
    from '../../../../../../types/sample-firmware-revision/SampleFirmwareInstallationStatus';
import SampleFirmwareRevisionWriteDTO
    from '../../../../../../types/sample-firmware-revision/SampleFirmwareRevisionWriteDTO';
import SampleHardwareRevision from '../../../../../../types/sample-hardware-revision/SampleHardwareRevision';
import { SampleFirmwareRevisionService } from '../../../../services/sample-firmware-revision.service';
import {
    UploadSampleFirmwareFileComponent
} from '../upload-sample-firmware-file/upload-sample-firmware-file.component';

@Component({
    selector: 'app-update-sample-firmware-revision-dialog',
    imports: [
        DialogModule,
        InputTextModule,
        ButtonModule,
        FormsModule,
        IconField,
        InputIcon,
        IftaLabelModule,
        Textarea,
        MultiSelectModule,
        UploadSampleFirmwareFileComponent,
        Select
    ],
    templateUrl: './update-sample-firmware-revision-dialog.component.html',
    styleUrl: './update-sample-firmware-revision-dialog.component.scss'
})
export class UpdateSampleFirmwareRevisionDialogComponent {
    @Input() public visible: boolean = false;
    @Output() public visibleChange: EventEmitter<boolean> = new EventEmitter<boolean>();
    @Input() public sampleFirmwareRevision: SampleFirmwareRevisionWriteDTO = new SampleFirmwareRevisionWriteDTO({});
    @Input() public allInstallationStatuses: SampleFirmwareInstallationStatus[] = [];
    @Input() public allAvailableSampleHardwareRevisions: SampleHardwareRevision[] = [];
    @Output() public sampleFirmwareRevisionUpdated: EventEmitter<SampleFirmwareRevisionWriteDTO> = new EventEmitter<SampleFirmwareRevisionWriteDTO>();

    public loading = false;

    public compatibleSampleHardwareRevisions: SampleHardwareRevision[] = [];
    public selectedStatus?: SampleFirmwareInstallationStatus;

    public selectedFile: File | null = null;
    public resetUpload = false;

    constructor(private sampleFirmwareRevisionService: SampleFirmwareRevisionService,
                private messageService: MessageService) {
    }

    /**
     * On changes to the component.
     */
    public ngOnChanges(): void {
        this.loadCompatibleHardwares();
        this.loadInstallationStatus();

    }

    /**
     * Loads compatible hardware versions upon initialisation.
     */
    private loadCompatibleHardwares() {
        const seletedCompatibleHardwarePCBVersions = new Set(
            this.sampleFirmwareRevision.hardwareRevisions.map(hr => hr.pcbVersion));
        this.compatibleSampleHardwareRevisions = this.allAvailableSampleHardwareRevisions.filter(hr =>
            seletedCompatibleHardwarePCBVersions.has(hr.pcbVersion)
        );
    }

    /**
     * Loads compatible installation statuses upon initialisation.
     */
    private loadInstallationStatus() {
        this.selectedStatus =
            this.allInstallationStatuses.find(s => s.name === this.sampleFirmwareRevision.installationStatus);
    }

    /**
     * Enables or Disables the save button based on required fields.
     */
    get saveDisabled(): boolean {
        return !this.sampleFirmwareRevision.description || !this.sampleFirmwareRevision.hardwareRevisions;
    }

    /**
     * Closes the create dialog box and resets the status variables.
     */
    public close() {
        this.visible = false;
        this.sampleFirmwareRevision = new SampleFirmwareRevisionWriteDTO({});
        this.selectedFile = null;
        this.resetUpload = true;
        this.compatibleSampleHardwareRevisions = [];
        this.visibleChange.emit(this.visible);
    }

    /**
     * Sets the file selected to be uploaded.
     */
    public onFileSelected(file: File | null) {
        this.selectedFile = file;
    }

    /**
     * Updates the firmware revision by sending the update request to the server.
     */
    public updateSampleFirmwareRevision() {
        this.loading = true;
        this.sampleFirmwareRevision.hardwareRevisions = this.compatibleSampleHardwareRevisions;
        this.sampleFirmwareRevision.installationStatus = this.selectedStatus?.name ?? '';
        this.sampleFirmwareRevisionService.updateSampleFirmwareRevision(this.sampleFirmwareRevision, this.selectedFile)
            .then(() => {
                this.sampleFirmwareRevisionUpdated.emit(this.sampleFirmwareRevision);
            }).catch((error) => {
            this.messageService.add({ severity: 'error', summary: 'Error', detail: error.message, life: 3000 });
        }).finally(() => {
            this.loading = false;
            this.close();
        });
    }
}
